import { z } from "zod";
import { pageOf } from "../common/pagination.js";
import { IdSchema, IsoDateTimeSchema } from "../common/primitives.js";
const TRANSMITTAL_LANGUAGES = ["fr", "ar"];
const TransmittalLanguageSchema = z.enum(TRANSMITTAL_LANGUAGES);
const TRANSMITTAL_ACK_CHANNELS = ["app", "link"];
const TransmittalAckChannelSchema = z.enum(TRANSMITTAL_ACK_CHANNELS);
const TRANSMITTAL_MAX_VERSIONS = 200;
const TRANSMITTAL_MAX_RECIPIENTS = 50;
const TRANSMITTAL_LINK_DAYS = 30;
const TRANSMITTAL_LIST_MAX = 50;
const transmittalNumberLabel = (n) => `BE-${String(n).padStart(3, "0")}`;
const TransmittalParamsSchema = z.strictObject({
  spaceId: IdSchema,
  transmittalId: IdSchema
});
const UserRecipientSchema = z.strictObject({ userId: IdSchema });
const EmailRecipientSchema = z.strictObject({
  email: z.email().max(254),
  name: z.string().trim().min(1).max(200).optional()
});
const TransmittalRecipientInputSchema = z.union([UserRecipientSchema, EmailRecipientSchema]);
const CreateTransmittalRequestSchema = z.strictObject({
  subject: z.string().trim().min(1).max(200),
  message: z.string().trim().min(1).max(2e3).optional(),
  language: TransmittalLanguageSchema,
  versionIds: z.array(IdSchema).min(1).max(TRANSMITTAL_MAX_VERSIONS).refine((ids) => new Set(ids).size === ids.length, "a version is listed twice"),
  recipients: z.array(TransmittalRecipientInputSchema).min(1).max(TRANSMITTAL_MAX_RECIPIENTS)
});
const TransmittalItemSchema = z.strictObject({
  id: IdSchema,
  documentId: IdSchema,
  versionId: IdSchema,
  ordinal: z.int().min(1),
  code: z.string().nullable(),
  title: z.string(),
  revisionIndex: z.string().nullable()
});
const TransmittalRecipientSchema = z.strictObject({
  id: IdSchema,
  kind: z.enum(["user", "email"]),
  userId: IdSchema.nullable(),
  name: z.string().nullable(),
  email: z.string().nullable(),
  emailedAt: IsoDateTimeSchema.nullable(),
  lastViewedAt: IsoDateTimeSchema.nullable(),
  acknowledgedAt: IsoDateTimeSchema.nullable(),
  acknowledgedVia: TransmittalAckChannelSchema.nullable()
});
const TransmittalSchema = z.strictObject({
  id: IdSchema,
  spaceId: IdSchema,
  number: z.int().min(1),
  numberLabel: z.string(),
  subject: z.string(),
  message: z.string().nullable(),
  language: TransmittalLanguageSchema,
  sentAt: IsoDateTimeSchema,
  sentBy: IdSchema,
  sentByName: z.string().nullable(),
  /** `preparing` until the pdf queue has stored the PDF. */
  pdfStatus: z.enum(["ready", "preparing"]),
  items: z.array(TransmittalItemSchema),
  recipients: z.array(TransmittalRecipientSchema)
});
const TransmittalSummarySchema = z.strictObject({
  id: IdSchema,
  number: z.int().min(1),
  numberLabel: z.string(),
  subject: z.string(),
  sentAt: IsoDateTimeSchema,
  sentBy: IdSchema,
  sentByName: z.string().nullable(),
  itemCount: z.int().min(0),
  recipientCount: z.int().min(0),
  acknowledgedCount: z.int().min(0)
});
const TransmittalListQuerySchema = z.strictObject({
  limit: z.coerce.number().int().min(1).max(TRANSMITTAL_LIST_MAX).default(TRANSMITTAL_LIST_MAX),
  cursor: z.string().min(1).max(512).optional()
});
const TransmittalListSchema = pageOf(TransmittalSummarySchema);
const TransmittalPdfUrlSchema = z.strictObject({
  status: z.enum(["ready", "preparing"]),
  url: z.url().nullable(),
  expiresAt: IsoDateTimeSchema.nullable()
});
const DiffusionEntrySchema = z.strictObject({
  transmittalId: IdSchema,
  number: z.int().min(1),
  numberLabel: z.string(),
  subject: z.string(),
  sentAt: IsoDateTimeSchema,
  versionId: IdSchema,
  revisionIndex: z.string().nullable(),
  recipientCount: z.int().min(0),
  acknowledgedCount: z.int().min(0),
  recipients: z.array(TransmittalRecipientSchema)
});
const DiffusionHistorySchema = z.strictObject({ items: z.array(DiffusionEntrySchema) });
const PublicTransmittalTokenSchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/);
const PublicTransmittalParamsSchema = z.strictObject({
  token: z.string().min(1).max(128)
});
const PublicTransmittalItemParamsSchema = z.strictObject({
  token: z.string().min(1).max(128),
  itemId: IdSchema
});
const PublicTransmittalSchema = z.strictObject({
  number: z.int().min(1),
  numberLabel: z.string(),
  subject: z.string(),
  message: z.string().nullable(),
  organizationName: z.string(),
  sentAt: IsoDateTimeSchema,
  language: TransmittalLanguageSchema,
  acknowledgedAt: IsoDateTimeSchema.nullable(),
  items: z.array(
    z.strictObject({
      id: IdSchema,
      code: z.string().nullable(),
      title: z.string(),
      revisionIndex: z.string().nullable()
    })
  )
});
export {
  CreateTransmittalRequestSchema,
  DiffusionEntrySchema,
  DiffusionHistorySchema,
  PublicTransmittalItemParamsSchema,
  PublicTransmittalParamsSchema,
  PublicTransmittalSchema,
  PublicTransmittalTokenSchema,
  TRANSMITTAL_ACK_CHANNELS,
  TRANSMITTAL_LANGUAGES,
  TRANSMITTAL_LINK_DAYS,
  TRANSMITTAL_LIST_MAX,
  TRANSMITTAL_MAX_RECIPIENTS,
  TRANSMITTAL_MAX_VERSIONS,
  TransmittalAckChannelSchema,
  TransmittalItemSchema,
  TransmittalLanguageSchema,
  TransmittalListQuerySchema,
  TransmittalListSchema,
  TransmittalParamsSchema,
  TransmittalPdfUrlSchema,
  TransmittalRecipientInputSchema,
  TransmittalRecipientSchema,
  TransmittalSchema,
  TransmittalSummarySchema,
  transmittalNumberLabel
};
//# sourceMappingURL=transmittals.js.map